import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { useEditorUI } from "PosterEditor/use-editor-ui";
import styles from "./toolbox-backdrop.module.css";
import { Toolbox } from "PosterEditor/sidebar/toolboxes/toolbox";
import {
  ToolboxComponentSection as Section,
  ToolboxSectionDivider as Divider,
} from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-section";
import { Backdrop, TextAlignment } from "PosterEditor/model/poster-project";
import { ToolboxComponentColorPicker as ColorPicker } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-color-picker";
import { ToolboxComponentSlider as Slider } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-slider";
import { legendTintColors } from "PosterEditor/model/poster-project";
import {
  defaultBackdrop,
  defaultBackdropStyle,
  defaultLabelStyle,
} from "PosterEditor/model/default-project-style";
import { useState, useEffect } from "react";
import { ToolboxComponentInput } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-input";
import { ToolboxComponentFontPicker as FontPicker } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-font-picker";
import { fontNames } from "PosterEditor/model/poster-project";
import { Size } from "PosterEditor/model/poster-types";
import {
  DeleteButton,
  BackButton,
  UIIconButton,
} from "PosterEditor/common-components/ui-icon-drawing/ui-icon-button";

interface ToolboxBackdropProps {
  backdrop: Backdrop;
}

export const ToolboxBackdrop = (props: ToolboxBackdropProps) => {
  const {
    setBackdrop: saveBackdropToProject,
    getBackdrop,
    removeBackdrop,
  } = usePosterProject();
  const { editorUIState, setEditingElement } = useEditorUI();

  const [backdrop, setBackdrop] = useState<Backdrop>(props.backdrop);

  useEffect(() => {
    const backdropID = props.backdrop.id;
    const backdrop = getBackdrop(backdropID) ?? defaultBackdrop;
    setBackdrop(backdrop);
  }, [
    getBackdrop,
    props.backdrop?.id,
    props.backdrop.position,
    props.backdrop.size,
  ]);

  const updateBackdrop = (style?: {
    backgroundColor?: string;
    backgroundOpacity?: number;
    cornerRadius?: number;
    borderWidth?: number;
    borderColor?: string;
    name?: string;
  }) => {
    if (!backdrop) {
      return;
    }
    const updatedBackdrop: Backdrop = {
      ...backdrop,
      name: style?.name ?? backdrop.name,
      // position: backdrop.position,
      style: {
        ...backdrop.style,
        backgroundColor:
          style?.backgroundColor ?? backdrop.style.backgroundColor,
        opacity: style?.backgroundOpacity ?? backdrop.style.opacity,
        cornerRadius: style?.cornerRadius ?? backdrop.style.cornerRadius,
        borderWidth: style?.borderWidth ?? backdrop.style.borderWidth,
        borderColor: style?.borderColor ?? backdrop.style.borderColor,
      },
    };
    setBackdrop(updatedBackdrop);
    saveBackdropToProject(updatedBackdrop);
  };

  const shortestSide = () => {
    const mapSize = editorUIState.mapSizeInPX;
    if (!mapSize) {
      return 50;
    }
    const backdropSize: Size = {
      width: backdrop.size.width * mapSize.width,
      height: backdrop.size.height * mapSize.height,
    };
    const borderWidth = backdrop.style.borderWidth;
    return Math.min(
      backdropSize.width - borderWidth,
      backdropSize.height - borderWidth
    );
  };

  return (
    <div className={styles.container}>
      <Header
        backdrop={backdrop}
        onClickBackButton={() => setEditingElement(undefined)}
        onClickDelete={() => {
          removeBackdrop(props.backdrop);
          setEditingElement(undefined);
        }}
        onChangeBackdropName={(name) => {
          updateBackdrop({ name });
        }}
      />
      <Toolbox collapsible={false} showTitleBar={false}>
        <div className={styles.paddedBodyContent}>
          <BackgroundControl
            backgroundColor={backdrop?.style.backgroundColor ?? "white"}
            backgroundOpacity={
              backdrop?.style.opacity ?? defaultBackdropStyle.opacity ?? 0.5
            }
            onChangeBackgroundColor={(color) => {
              updateBackdrop({ backgroundColor: color });
            }}
            onChangeBackgroundOpacity={(opacity) => {
              updateBackdrop({ backgroundOpacity: opacity });
            }}
          />
          <Divider />
          <BorderControl
            color={backdrop?.style.borderColor ?? "white"}
            onChangeColor={(color) => {
              updateBackdrop({ borderColor: color });
            }}
            width={backdrop?.style.borderWidth ?? 2}
            onChangeWidth={(width) => {
              updateBackdrop({ borderWidth: width });
            }}
            cornerRadius={backdrop?.style.cornerRadius ?? 3}
            onChangeCornerRadius={(cornerRadius) => {
              updateBackdrop({ cornerRadius: cornerRadius });
            }}
            rectShortestSide={shortestSide()}
          />
        </div>
      </Toolbox>
    </div>
  );
};

interface HeaderProps {
  backdrop: Backdrop;
  onChangeBackdropName: (name: string) => void;
  onClickBackButton: () => void;
  onClickDelete: () => void;
}

const Header = (props: HeaderProps) => {
  return (
    <div className={styles.header}>
      <div className={styles.headerLeading}>
        <BackButton onClick={props.onClickBackButton} />
        <TextContentControl
          text={props.backdrop.name}
          onChangeText={props.onChangeBackdropName}
          onClickDeleteLabel={() => {}}
        />
      </div>
      <div className={styles.headerTrailing}>
        <DeleteButton onClick={props.onClickDelete} />
      </div>
    </div>
  );
};

interface BackgroundControlProps {
  backgroundColor: string;
  backgroundOpacity: number;
  onChangeBackgroundColor: (color: string) => void;
  onChangeBackgroundOpacity: (opacity: number) => void;
}

const BackgroundControl = (props: BackgroundControlProps) => {
  const [isColorExpanded, setIsColorExpanded] = useState(false);

  return (
    <Section title="Background">
      <div className={styles.toolboxRowContainer}>
        <ColorPicker
          showSwatch={true}
          colors={legendTintColors}
          selectedColor={props.backgroundColor}
          onSelectColor={props.onChangeBackgroundColor}
          isExpanded={isColorExpanded}
          onToggleExpand={() => setIsColorExpanded((prev) => !prev)}
        />
        <Slider
          title="Opacity"
          min={0}
          max={100}
          value={props.backgroundOpacity * 100}
          onChange={(opacityPerc) => {
            const opacity = opacityPerc / 100;
            props.onChangeBackgroundOpacity(opacity);
          }}
        />
      </div>
    </Section>
  );
};

interface BorderControlProps {
  color: string;
  onChangeColor: (color: string) => void;
  width: number;
  onChangeWidth: (width: number) => void;
  cornerRadius: number;
  onChangeCornerRadius: (cornerRadius: number) => void;
  rectShortestSide: number;
}

const BorderControl = (props: BorderControlProps) => {
  const [isColorExpanded, setIsColorExpanded] = useState(false);

  return (
    <Section title="Border">
      <div className={styles.toolboxRowContainer}>
        <ColorPicker
          showSwatch={true}
          colors={legendTintColors}
          selectedColor={props.color}
          onSelectColor={props.onChangeColor}
          isExpanded={isColorExpanded}
          onToggleExpand={() => setIsColorExpanded((prev) => !prev)}
        />
        <Slider
          title="Width"
          min={0}
          max={30}
          value={props.width}
          onChange={(value) => {
            props.onChangeWidth(value);
          }}
        />
      </div>
      <Slider
        title="Corner radius"
        min={0}
        max={props.rectShortestSide / 2}
        value={props.cornerRadius}
        onChange={(value) => {
          props.onChangeCornerRadius(value);
        }}
      />
    </Section>
  );
};

interface TextContentControlProps {
  text: string;
  onChangeText: (text: string) => void;
  onClickDeleteLabel: () => void;
}

const TextContentControl = (props: TextContentControlProps) => {
  return (
    <Section>
      <ToolboxComponentInput
        text={props.text}
        onChangeText={props.onChangeText}
        autoFocusOnMount={true}
      />
    </Section>
  );
};
